import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, Box, Typography, TextField, Button, IconButton, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createDistrictAction,
  clearCreateDistrictAction,
  getDistrictByIdAction,
  updateDistrictAction,
  clearUpdateDistrictAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllUserAction,
} from "../../../store/actions";

const AddDistrict = ({ open, onClose, isEdit, districtId, onSuccess }) => {
  const dispatch = useDispatch();

  const { successData: usersData, loading: usersLoading } = useSelector((state) => state.getAllUserReducer || {});
  const { successData: zonesData, loading: zonesLoading } = useSelector((state) => state.getAllZonesReducer || {});
  const { successData: regionsData, loading: regionsLoading } = useSelector((state) => state.getAllRegionsReducer || {});
  const { successData: areasData, loading: areasLoading } = useSelector((state) => state.getAllAreasReducer || {});
  const { successData: districtByIdData, loading: districtByIdLoading, error: districtByIdError } = useSelector((state) => state.getDistrictByIdReducer || {});
  const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createDistrictReducer || {});
  const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateDistrictReducer || {});

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (usersData && usersData.data && Array.isArray(usersData.data.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const soUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.filter((u) => {
      const role = (u.role || u.roleType || u.userRole || u.role_name || "").toString().toUpperCase();
      return role === "SO";
    });
  }, [users]);

  const zones = useMemo(() => {
    if (Array.isArray(zonesData)) return zonesData;
    if (zonesData && zonesData.data && Array.isArray(zonesData.data.zones)) return zonesData.data.zones;
    return [];
  }, [zonesData]);

  const regions = useMemo(() => {
    if (Array.isArray(regionsData)) return regionsData;
    if (regionsData && regionsData.data && Array.isArray(regionsData.data.regions)) return regionsData.data.regions;
    return [];
  }, [regionsData]);

  const areas = useMemo(() => {
    if (Array.isArray(areasData)) return areasData;
    if (areasData && areasData.data && Array.isArray(areasData.data.areas)) return areasData.data.areas;
    return [];
  }, [areasData]);

  const districtSchema = Yup.object({
    name: Yup.string().trim().required("District name is required"),
    zone_id: Yup.number().typeError("Zone ID must be a number").required("Zone ID is required"),
    region_id: Yup.number().typeError("Region ID must be a number").required("Region ID is required"),
    area_id: Yup.number().typeError("Area ID must be a number").required("Area ID is required"),
  });

  // Helper to normalize district names (remove spaces, lowercase)
  const normalizeDistrictName = (name) => (name || "").replace(/\s+/g, "").toLowerCase();

  // Get all existing district names for duplicate check
  const allDistricts = useSelector((state) => {
    // Try to get from getAllDistrictsReducer or similar, fallback to []
    if (state.getAllDistrictsReducer && Array.isArray(state.getAllDistrictsReducer.successData)) {
      return state.getAllDistrictsReducer.successData;
    }
    if (state.getAllDistrictsReducer && state.getAllDistrictsReducer.successData && Array.isArray(state.getAllDistrictsReducer.successData.data?.districts)) {
      return state.getAllDistrictsReducer.successData.data.districts;
    }
    return [];
  });

  // State for duplicate error
  const [duplicateError, setDuplicateError] = useState("");

  const formik = useFormik({
    initialValues: { name: "", zone_id: "", region_id: "", area_id: "", incharge_user_id: "" },
    validationSchema: districtSchema,
    enableReinitialize: false,
    onSubmit: (values) => {
      const newNameNorm = normalizeDistrictName(values.name);
      // For edit, allow updating the same district, but block if another has the same name
      const duplicate = allDistricts.some((d) => {
        const dName = d?.name || d?.district_name || d?.districtName || "";
        const dId = d?.districtId || d?.id || d?.district_id;
        // If editing, skip the current district
        if (isEdit && String(dId) === String(districtId)) return false;
        return normalizeDistrictName(dName) === newNameNorm;
      });
      if (duplicate) {
        setDuplicateError("District with this name already exists!");
        return;
      }
      setDuplicateError("");
      const payload = {
        name: values.name,
        incharge_user_id: values.incharge_user_id ? values.incharge_user_id : null,
        zone_id: Number(values.zone_id),
        region_id: Number(values.region_id),
        area_id: Number(values.area_id),
      };
      if (isEdit && districtId) {
        dispatch(updateDistrictAction({ ...payload, endPoint: `geography/district/${districtId}` }));
      } else {
        dispatch(createDistrictAction(payload));
      }
    },
  });

  // Check for duplicate as user types
  useEffect(() => {
    if (formik.values.name) {
      const newNameNorm = normalizeDistrictName(formik.values.name);
      const duplicate = allDistricts.some((d) => {
        const dName = d?.name || d?.district_name || d?.districtName || "";
        const dId = d?.districtId || d?.id || d?.district_id;
        if (isEdit && String(dId) === String(districtId)) return false;
        return normalizeDistrictName(dName) === newNameNorm;
      });
      if (duplicate) {
        setDuplicateError("District with this name already exists!");
      } else {
        setDuplicateError("");
      }
    } else {
      setDuplicateError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name, allDistricts, isEdit, districtId]);

  useEffect(() => {
    if (open) {
      dispatch(getAllUserAction());
      dispatch(getAllZonesAction());
      dispatch(getAllRegionsAction());
      dispatch(getAllAreasAction());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && isEdit && districtId) {
      dispatch(getDistrictByIdAction({ endPoint: `geography/district/${districtId}` }));
    }
  }, [open, isEdit, districtId, dispatch]);

  useEffect(() => {
    if (open && isEdit && districtByIdData && !districtByIdLoading && !districtByIdError) {
      const d = districtByIdData?.data?.district || districtByIdData?.data || districtByIdData;
      const prefill = {
        name: d?.name || d?.district_name || d?.districtName || "",
        zone_id: d?.zone_id ?? d?.zoneId ?? d?.zoneID ?? "",
        region_id: d?.region_id ?? d?.regionId ?? d?.regionID ?? "",
        area_id: d?.area_id ?? d?.areaId ?? d?.areaID ?? "",
        incharge_user_id: d?.incharge_user_id || d?.inchargeUserId || d?.userId || "",
      };
      formik.setValues(prefill);
    }
  }, [open, isEdit, districtByIdData, districtByIdLoading, districtByIdError]);

  useEffect(() => {
    if (!open) return;
    if (createSuccess) {
      toast.success("District created successfully!");
      formik.resetForm();
      dispatch(clearCreateDistrictAction());
      onSuccess?.();
      onClose();
    }
    if (createError) {
      toast.error(createErrorInfo || "Failed to create district");
      dispatch(clearCreateDistrictAction());
    }
  }, [open, createSuccess, createError, createErrorInfo, dispatch, formik, onClose, onSuccess]);

  useEffect(() => {
    if (!open) return;
    if (updateSuccess) {
      toast.success("District updated successfully!");
      formik.resetForm();
      dispatch(clearUpdateDistrictAction());
      onSuccess?.();
      onClose();
    }
    if (updateError) {
      toast.error(updateErrorInfo || "Failed to update district");
      dispatch(clearUpdateDistrictAction());
    }
  }, [open, updateSuccess, updateError, updateErrorInfo, dispatch, formik, onClose, onSuccess]);

  const loading = createLoading || updateLoading || districtByIdLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 4 } } } }}>
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography fontSize={28} fontWeight={700}>District Information</Typography>
        <Typography fontSize={14} color="text.secondary">{isEdit ? "Update District" : "Create new District"}</Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Zone</Typography>
            <Autocomplete
              options={zones.map((z) => ({ id: z.zoneId || z.id || z.zone_id || z.zoneID, name: z.zone_name || z.zoneName || z.name || String(z.zoneId || z.id || z.zone_id || z.zoneID || "") })).filter((o) => o.id)}
              value={(function () {
                const currentId = formik.values.zone_id;
                if (!currentId) return null;
                const found = zones.find((z) => {
                  const zid = z.zoneId || z.id || z.zone_id || z.zoneID;
                  return String(zid) === String(currentId);
                });
                return found
                  ? { id: found.zoneId || found.id || found.zone_id || found.zoneID, name: found.zone_name || found.zoneName || found.name || String(found.zoneId || found.id || found.zone_id || found.zoneID || "") }
                  : null;
              })()}
              onChange={(_, newValue) => {
                const newZoneId = newValue ? Number(newValue.id) : "";
                const prevZoneId = formik.values.zone_id;
                formik.setFieldValue("zone_id", newZoneId);
                if (String(prevZoneId) !== String(newZoneId)) {
                  formik.setFieldValue("region_id", "");
                  formik.setFieldValue("area_id", "");
                }
              }}
              loading={zonesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={zonesLoading ? "Loading zones..." : "Select a zone"}
                  fullWidth
                  size="small"
                  error={formik.touched.zone_id && Boolean(formik.errors.zone_id)}
                  helperText={formik.touched.zone_id && formik.errors.zone_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Region</Typography>
            <Autocomplete
              options={regions
                .map((r) => ({ id: r.regionId || r.id || r.region_id || r.regionID, name: r.region_name || r.regionName || r.name || String(r.regionId || r.id || r.region_id || r.regionID || ""), zoneId: r.zone_id || r.zoneId || r.zoneID || r.parentZoneId }))
                .filter((o) => o.id && formik.values.zone_id && String(o.zoneId) === String(formik.values.zone_id))}
              value={(function () {
                const currentId = formik.values.region_id;
                if (!currentId) return null;
                const found = regions.find((r) => {
                  const rid = r.regionId || r.id || r.region_id || r.regionID;
                  return String(rid) === String(currentId);
                });
                return found
                  ? { id: found.regionId || found.id || found.region_id || found.regionID, name: found.region_name || found.regionName || found.name || String(found.regionId || found.id || found.region_id || found.regionID || "") }
                  : null;
              })()}
              onChange={(_, newValue) => {
                formik.setFieldValue("region_id", newValue ? Number(newValue.id) : "");
                formik.setFieldValue("area_id", "");
              }}
              loading={regionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={!formik.values.zone_id ? "Select a zone first" : regionsLoading ? "Loading regions..." : "Select a region"}
                  fullWidth
                  size="small"
                  error={formik.touched.region_id && Boolean(formik.errors.region_id)}
                  helperText={formik.touched.region_id && formik.errors.region_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              disabled={!formik.values.zone_id}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Area</Typography>
            <Autocomplete
              options={areas
                .map((a) => ({ id: a.areaId || a.id || a.area_id || a.areaID, name: a.area_name || a.areaName || a.name || String(a.areaId || a.id || a.area_id || a.areaID || ""), regionId: a.region_id || a.regionId || a.regionID }))
                .filter((o) => o.id && formik.values.region_id && String(o.regionId) === String(formik.values.region_id))}
              value={(function () {
                const currentId = formik.values.area_id;
                if (!currentId) return null;
                const found = areas.find((a) => {
                  const aid = a.areaId || a.id || a.area_id || a.areaID;
                  return String(aid) === String(currentId);
                });
                return found
                  ? { id: found.areaId || found.id || found.area_id || found.areaID, name: found.area_name || found.areaName || found.name || String(found.areaId || found.id || found.area_id || found.areaID || "") }
                  : null;
              })()}
              onChange={(_, newValue) => {
                formik.setFieldValue("area_id", newValue ? Number(newValue.id) : "");
              }}
              loading={areasLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={!formik.values.region_id ? "Select a region first" : areasLoading ? "Loading areas..." : "Select an area"}
                  fullWidth
                  size="small"
                  error={formik.touched.area_id && Boolean(formik.errors.area_id)}
                  helperText={formik.touched.area_id && formik.errors.area_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              disabled={!formik.values.region_id}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>District Name</Typography>
            <TextField
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              size="small"
              placeholder="Enter District"
              error={!!duplicateError || (formik.touched.name && Boolean(formik.errors.name))}
              helperText={formik.touched.name && formik.errors.name}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
            {duplicateError && (
              <Box mt={1}>
                <Box component={require("@mui/material").Paper} elevation={2} sx={{ p: 1.5, bgcolor: '#fff3e0', color: '#d84315', borderRadius: 2, fontWeight: 600, fontSize: 14 }}>
                  {duplicateError}
                </Box>
              </Box>
            )}
          </Box>

          <Box mb={4}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Incharge User (SO)</Typography>
            <Autocomplete
              options={soUsers}
              getOptionLabel={(option) => option.name || option.fullName || option.email || ""}
              isOptionEqualToValue={(option, value) => (option.userId || option.id) === (value.userId || value.id)}
              value={soUsers.find((u) => (u.userId || u.id) === formik.values.incharge_user_id) || null}
              onChange={(_, selected) => {
                formik.setFieldValue("incharge_user_id", selected ? (selected.userId || selected.id || "") : "");
              }}
              onBlur={() => formik.setFieldTouched("incharge_user_id", true)}
              disabled={usersLoading}
              loading={usersLoading}
              noOptionsText={soUsers.length === 0 ? "No SO users available" : "No match found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={usersLoading ? "Loading users..." : "Search and select incharge"}
                  error={formik.touched.incharge_user_id && Boolean(formik.errors.incharge_user_id)}
                  helperText={formik.touched.incharge_user_id && formik.errors.incharge_user_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection="row">
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: 3, fontWeight: 600, p: "10px 0px", color: "#000", border: "1px solid #000", textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !!duplicateError}
              sx={{ borderRadius: 3, p: "10px 0px", fontWeight: 600, textTransform: "none", bgcolor: "#5B4CF5", boxShadow: "0px 8px 20px rgba(91,76,245,0.3)", "&:hover": { bgcolor: "#4a3de0" } }}
            >
              {loading ? (isEdit ? "Updating..." : "Submitting...") : isEdit ? "Update District" : "Submit Data"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

AddDistrict.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  districtId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

AddDistrict.defaultProps = {
  isEdit: false,
  districtId: "",
  onSuccess: undefined,
};

export default AddDistrict;